package one.healthium.health_tools.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import loco.GENERAL_OPEN
import loco.GENERAL_STEPS
import loco.HEALTH_TOOLS_SCREEN_TITLE
import loco.TODAY_LOG_WATER_BUTTON
import loco.TODAY_LOG_WEIGHT_BUTTON
import one.healthium.base.utils.Formatters.formatWeight
import one.healthium.base.utils.relativeDate
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.health_tools.ui.models.DailyStepsUiModel
import one.healthium.health_tools.ui.models.UpdateWeightUiModel
import one.healthium.health_tools.ui.models.WaterIntakeUiModel
import one.healthium.loco.resolve
import one.healthium.meal_plans.domain.MealPlanInfoModel
import one.healthium.ui.compose.components.AppIcons
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppTopBar
import one.healthium.ui.compose.modifier.adaptiveWidthTwoPane
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface HealthToolsEventDelegate {
    fun onLogWeightClicked()
    fun onLogWaterClicked()
    fun onMealPlanClicked(mealPlanId: String)
}

@Composable
fun HealthToolsScreen(eventDelegate: HealthToolsEventDelegate) {
    val viewModel = koinViewModel<HealthToolsViewModel>()
    val state by viewModel.state.collectAsState()

    HealthToolsScreenContent(
        state = state,
        onLogWeightClicked = eventDelegate::onLogWeightClicked,
        onLogWaterClicked = eventDelegate::onLogWaterClicked,
        onMealPlanClicked = { eventDelegate.onMealPlanClicked(it.id) },
    )
}

@Composable
private fun HealthToolsScreenContent(
    state: HealthToolsUiState,
    onLogWeightClicked: () -> Unit,
    onLogWaterClicked: () -> Unit,
    onMealPlanClicked: (MealPlanInfoModel) -> Unit,
) {
    AppScaffold(
        topBar = {
            AppTopBar(
                title = {
                    Text(stringResource(loco.Res.string.HEALTH_TOOLS_SCREEN_TITLE))
                }
            )
        }
    ) {
        Box(modifier = Modifier.fillMaxSize().padding(it)) {
            Column(
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .adaptiveWidthTwoPane()
                    .padding(horizontal = 12.dp, vertical = 24.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                LogWeightControl(
                    latestWeightLog = state.latestWeightLog,
                    isMetric = state.isMetric,
                    onLogWeightClicked = onLogWeightClicked,
                )
                Spacer(Modifier.height(12.dp))
                LogWaterControl(
                    todayWaterIntake = state.todayWaterIntake,
                    isMetric = state.isMetric,
                    onLogWaterClicked = onLogWaterClicked,
                )
                if (state.todayStepsCounter != null) {
                    Spacer(Modifier.height(12.dp))
                    StepsCounterControl(
                        steps = state.todayStepsCounter
                    )
                }
                Spacer(Modifier.height(12.dp))
                MealPlanControl(
                    mealPlan = state.mealPlan,
                    onClick = onMealPlanClicked,
                )
            }
        }
    }
}

@Composable
private fun LogWeightControl(
    latestWeightLog: UpdateWeightUiModel?,
    isMetric: Boolean,
    onLogWeightClicked: () -> Unit,
) {
    var weightValue: String? = null
    var dateValue: String? = null

    if (latestWeightLog != null) {
        weightValue = latestWeightLog.currentWeight.formatWeight(isMetric).resolve()
        dateValue = latestWeightLog.date?.relativeDate()?.resolve().orEmpty()
    }

    BaseLogControl(
        containerColor = AppTheme.colors.primary,
        icon = AppIcons.icFileChecked,
        button = stringResource(loco.Res.string.TODAY_LOG_WEIGHT_BUTTON),
        onClick = onLogWeightClicked,
    ) {
        Column(
            modifier = Modifier.weight(1f),
        ) {
            if (!weightValue.isNullOrBlank()) {
                Text(
                    text = weightValue,
                    style = AppTheme.textStyles.secondary_17_500,
                    color = AppTheme.colors.onPrimary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            if (!dateValue.isNullOrBlank()) {
                Text(
                    text = dateValue,
                    style = AppTheme.textStyles.secondary_17_400,
                    color = AppTheme.colors.onPrimary.copy(alpha = 0.3f),
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun LogWaterControl(
    todayWaterIntake: WaterIntakeUiModel?,
    isMetric: Boolean,
    onLogWaterClicked: () -> Unit,
) {
    LogControl(
        containerColor = AppTheme.colors.primary,
        contentColor = AppTheme.colors.onPrimary,
        icon = AppIcons.icBottle,
        contentPrimary = "${todayWaterIntake?.currentValue?.toVolumeString(isMetric)}" +
                " / " +
                "${todayWaterIntake?.dailyAmount?.toVolumeString(isMetric)}",
        contentSecondary = " ${isMetric.toVolumeUnit().resolve()}",
        button = stringResource(loco.Res.string.TODAY_LOG_WATER_BUTTON),
        onClick = onLogWaterClicked,
    )
}

@Composable
fun StepsCounterControl(steps: DailyStepsUiModel) {
    LogControl(
        containerColor = AppTheme.colors.primary,
        contentColor = AppTheme.colors.onPrimary,
        icon = AppIcons.icSteps,
        contentPrimary = "${steps.steps} / ${steps.goal}",
        contentSecondary = " ${stringResource(loco.Res.string.GENERAL_STEPS)}",
    )
}

@Composable
private fun MealPlanControl(mealPlan: MealPlanInfoModel?, onClick: (MealPlanInfoModel) -> Unit) {
    BaseLogControl(
        containerColor = AppTheme.colors.primary,
        icon = AppIcons.icPizza,
        button = stringResource(loco.Res.string.GENERAL_OPEN),
        onClick = {
            if (mealPlan != null) {
                onClick(mealPlan)
            }
        },
    ) {
        Text(
            modifier = Modifier.weight(1f),
            text = mealPlan?.name?.resolve() ?: "",
            style = AppTheme.textStyles.secondary_17_500,
            color = AppTheme.colors.onPrimary,
            overflow = TextOverflow.Ellipsis,
            maxLines = 2,
        )
    }
}

